import { Fragment, useState } from 'react'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackButton } from '@/components/topbar'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Card } from '@/components/ui/card'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AlertBadge } from '@/components/ui/alert-badge'
import { DetailSkeleton } from '@/components/ui/skeleton-presets'
import { EmptyState } from '@/components/EmptyState'
import { ArrowLeft, ArrowRight, Pencil, PlaneTakeoff } from '@/components/icons'
import { SwipeableRow } from '@/components/ui/swipeable-row'
import { FlightLogDrawer } from '@/components/FlightLogDrawer'
import { DraftLogPill } from '@/components/flight-log/FlightLogNumberBadge'
import { useAircraftGetById } from '@/lib/hooks'
import { useFlightLogGetByAircraft } from '@/lib/hooks/flightLog'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { useIsPilot } from '@/hooks/useIsPilot'

export const Route = createFileRoute('/_auth/aircraft/$aircraftId_/flight-logs')({
  component: AircraftFlightLogsRoute,
})

// Lean list DTO from flightLog.getByAircraft. Leg-based: a departed leg whose
// log was never started appears with a null logNumber and 'notStarted'.
interface FlightLogListItem {
  id: string
  tripLegId: string
  tripId: string
  logNumber: number | null
  status: string | null
  lifecycle: 'notStarted' | 'inProgress' | 'awaitingLanding' | 'complete'
  submittedAt: Date | string | null
  departureAirportIcao: string
  arrivalAirportIcao: string
  departureDateTime: Date | string
  defectDeferred: boolean
  defectSummary: string | null
  signedOffBy: string | null
}

type LogFilter = 'all' | 'defects' | 'inProgress' | 'complete'

function matchesFilter(log: FlightLogListItem, filter: LogFilter): boolean {
  switch (filter) {
    case 'all':
      return true
    case 'defects':
      return log.defectDeferred
    case 'inProgress':
      // Anything the pilot still owes: never started, or started but unsigned
      return log.lifecycle !== 'complete'
    case 'complete':
      return log.lifecycle === 'complete'
  }
}

// The list is keyed on the flight day (the leg's departure), not on when the
// log was signed off. This matches the server's sort key so month groups stay
// in order.
function logDate(log: FlightLogListItem): Date | string {
  return log.departureDateTime
}

function AircraftFlightLogsRoute() {
  const { aircraftId } = Route.useParams()
  const navigate = useNavigate()
  const isDesktop = useIsDesktop()
  const { isAdmin } = useIsAdmin()
  const { isPilot } = useIsPilot()
  const { data: aircraft } = useAircraftGetById(aircraftId)
  const { data: logs, isLoading } = useFlightLogGetByAircraft(aircraftId, {
    enabled: isAdmin || isPilot,
  })
  const [filter, setFilter] = useState<LogFilter>('all')
  // Log sticks around after close so the drawer can animate out
  const [selectedLog, setSelectedLog] = useState<FlightLogListItem | null>(null)
  const [isLogOpen, setIsLogOpen] = useState(false)
  // Only one row may be swiped open at a time
  const [openRowId, setOpenRowId] = useState<string | null>(null)

  const openLog = (log: FlightLogListItem) => {
    setSelectedLog(log)
    setIsLogOpen(true)
  }

  const allLogs = (logs ?? []) as FlightLogListItem[]
  const filteredLogs = allLogs.filter((log) => matchesFilter(log, filter))

  // Group into month buckets. Map insertion order preserves the server's
  // newest-first order, and keying by label means a month can never repeat.
  const monthMap = new Map<string, FlightLogListItem[]>()
  for (const log of filteredLogs) {
    const label = dayjs.utc(logDate(log)).format('MMM YYYY')
    const group = monthMap.get(label)
    if (group) {
      group.push(log)
    } else {
      monthMap.set(label, [log])
    }
  }
  const monthGroups = Array.from(monthMap, ([label, logs]) => ({ label, logs }))

  const body = (
    <>
      {isLoading ? (
        <DetailSkeleton rows={5} />
      ) : (
        <div className="flex flex-col gap-3">
          <Tabs value={filter} onValueChange={(value) => setFilter(value as LogFilter)}>
            <TabsList className="w-full">
              <TabsTrigger value="all">
                All{allLogs.length > 0 ? ` ${allLogs.length}` : ''}
              </TabsTrigger>
              <TabsTrigger value="defects">Defects</TabsTrigger>
              <TabsTrigger value="inProgress">In Progress</TabsTrigger>
              <TabsTrigger value="complete">Complete</TabsTrigger>
            </TabsList>
          </Tabs>

          {!filteredLogs.length ? (
            <EmptyState
              icon={PlaneTakeoff}
              title={allLogs.length ? 'No matching logs' : 'No flight logs'}
              description={
                allLogs.length
                  ? 'Try a different filter.'
                  : 'Flight logs filed for this aircraft will appear here.'
              }
            />
          ) : (
            monthGroups.map((group) => (
              <Fragment key={group.label}>
                <div className="flex justify-center">
                  <span className="rounded-full bg-surface-L2 px-3 py-1 text-xs font-semibold text-content-primary-subtle">
                    {group.label}
                  </span>
                </div>
                <Card className="flex flex-col gap-0 p-0 overflow-hidden">
                  {group.logs.map((log, idx) => (
                    <Fragment key={log.id}>
                      {/* Swipe left (pilots) to reveal Edit. The component's
                          primary action slot is repurposed as the edit button
                          since this row has no destructive action. */}
                      <SwipeableRow
                        isOpen={openRowId === log.id}
                        onOpenChange={(open) => setOpenRowId(open ? log.id : null)}
                        onDelete={() => {
                          setOpenRowId(null)
                          openLog(log)
                        }}
                        actionLabel="Edit"
                        actionIcon={<Pencil />}
                        actionClassName="bg-content-info-bold"
                        variant="icon"
                        foregroundClassName="bg-surface-L0"
                        disabled={!isPilot}
                      >
                        <FlightLogRow log={log} onClick={() => openLog(log)} />
                      </SwipeableRow>
                      {idx < group.logs.length - 1 && <div className="h-px bg-border-L1 mx-4" />}
                    </Fragment>
                  ))}
                </Card>
              </Fragment>
            ))
          )}
        </div>
      )}

      {selectedLog && (
        <FlightLogDrawer
          isOpen={isLogOpen}
          onClose={() => setIsLogOpen(false)}
          tripLegId={selectedLog.tripLegId}
          readOnly={!isPilot}
          initialSection={selectedLog.defectDeferred ? 'defects' : undefined}
          onViewTrip={
            selectedLog.tripId
              ? () => {
                  const tripId = selectedLog.tripId
                  const search = { legId: selectedLog.tripLegId }
                  if (isAdmin) {
                    navigate({ to: '/operations/trips/$tripId', params: { tripId }, search } as any)
                  } else {
                    navigate({ to: '/schedule/$tripId', params: { tripId }, search } as any)
                  }
                }
              : undefined
          }
        />
      )}
    </>
  )

  const breadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => navigate({ to: '/aircraft/$aircraftId', params: { aircraftId } })}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to aircraft"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        <Link to="/aircraft/$aircraftId" params={{ aircraftId }} className="hover:underline">
          {aircraft?.registration ?? 'Aircraft'}
        </Link>
        <span className="text-content-primary-subtle"> / Flight Logs</span>
      </span>
    </span>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader title="Flight Logs" breadcrumb={breadcrumb} />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout topBar={<TopBarBackButton title="Flight Logs" />}>
      {body}
    </AppLayout>
  )
}

function FlightLogRow({ log, onClick }: { log: FlightLogListItem; onClick: () => void }) {
  const date = dayjs.utc(logDate(log))
  const badgeVariant = log.defectDeferred
    ? 'error'
    : log.lifecycle === 'complete'
      ? 'valid'
      : 'warning'

  return (
    <button
      onClick={onClick}
      className="w-full text-left flex flex-col gap-2 px-4 py-3 bg-surface-L0"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="flex min-w-0 items-center gap-2 text-sm leading-5">
          {log.logNumber !== null &&
            (log.lifecycle === 'complete' ? (
              <span className="shrink-0 font-bold text-content-primary-bold">{log.logNumber}</span>
            ) : (
              <DraftLogPill logNumber={log.logNumber} />
            ))}
          <span className="flex shrink-0 items-center gap-1 text-content-primary-subtle">
            <span>{log.departureAirportIcao}</span>
            <ArrowRight className="size-3 text-content-primary-subtle-mid" />
            <span>{log.arrivalAirportIcao}</span>
          </span>
          <span className="truncate text-content-primary-subtle">
            • {date.format('D MMM, HH:mm')}
          </span>
        </p>
        <AlertBadge variant={badgeVariant} />
      </div>

      {log.defectDeferred ? (
        <div className="flex items-center gap-2">
          <span className="shrink-0 rounded-full bg-background-negative-subtle px-2 py-0.5 text-xs font-semibold text-content-negative-bold">
            Defect Deferred
          </span>
          {log.defectSummary && (
            <span className="min-w-0 truncate text-sm font-medium text-content-primary-bold">
              {log.defectSummary}
            </span>
          )}
        </div>
      ) : (
        <p className="text-xs font-semibold leading-4 text-content-primary-subtle">
          {log.lifecycle === 'complete'
            ? log.signedOffBy
              ? `Signed off · ${log.signedOffBy}`
              : 'Signed off'
            : log.lifecycle === 'notStarted'
              ? 'Not started · log not yet filed'
              : 'In progress · awaiting post-flight sign-off'}
        </p>
      )}
    </button>
  )
}
